import {FieldValues} from "react-hook-form/dist/types/fields";
import {Control, PathValue, UseFormTrigger, Validate} from "react-hook-form/dist/types";
import {FieldPath, Path, UseFormGetValues, UseFormSetValue} from "react-hook-form";
import React from "react";
import {InputCleave} from "@components/wrapperComponents/InputEx";
import {InputCleave2} from "@components/wrapperComponents/InputCleave2";
import {NumberFormatValues} from "react-number-format";
import {FieldPathValue} from "react-hook-form/dist/types/path";
import {useRangeValidator} from "@commons/hooks/useRangeValidator";

type ValidationRules<T extends FieldValues, TFieldName extends FieldPath<T> = FieldPath<T>> =
    Validate<FieldPathValue<T, TFieldName>, T>
    | Record<string, Validate<FieldPathValue<T, TFieldName>, T>>;

interface RangeHHProps<T extends FieldValues, TFieldName extends FieldPath<T> = FieldPath<T>> {
    control: Control<T>,
    trigger: UseFormTrigger<T>,
    getValues: UseFormGetValues<T>,
    setValue: UseFormSetValue<T>,
    nameInizio: FieldPath<T>,
    nameFine: FieldPath<T>,
    nameQuantita: FieldPath<T>,
    defaultValueInizio?: PathValue<T, Path<T>>,
    defaultValueFine?: PathValue<T, Path<T>>,
    defaultValueQuantita?: PathValue<T, Path<T>>,
    disableFields?: boolean,
    optionalFields?: boolean,
    validate?: ValidationRules<T, TFieldName>, //(v: any) => boolean | string,
    errorPortalId?: string,
    onChangeRange?: (inizio: number, fine: number, durata: number) => void,
    onNotANumber?: (inizio: number, fine: number, durata: number) => void
    children: (hhInizio: JSX.Element, hhFine: JSX.Element, quantita: JSX.Element) => React.ReactNode,
    //children: (HHInizio: React.FC, HHFine: React.FC) => React.ReactNode,
    showLabel?: boolean,
    labelsClassName?: string,
    quantitaOrarieIn?: 'CENTESIMI' | 'MINUTI',
}

function RangeHH<T extends FieldValues, TFieldName extends FieldPath<T> = FieldPath<T>>({
                                                                                            control,
                                                                                            nameInizio,
                                                                                            nameFine,
                                                                                            nameQuantita,
                                                                                            defaultValueInizio,
                                                                                            defaultValueFine,
                                                                                            defaultValueQuantita,
                                                                                            disableFields = false,
                                                                                            optionalFields = false,
                                                                                            validate,
                                                                                            errorPortalId,
                                                                                            onChangeRange,
                                                                                            onNotANumber,
                                                                                            children,
                                                                                            showLabel = true, labelsClassName = '',
                                                                                            quantitaOrarieIn = 'CENTESIMI',
                                                                                        }: RangeHHProps<T, TFieldName>) {
    const options = {
        time: true, timePattern: ['h', 'm'], blocks: [2, 2],
        delimiter: ':', timeFormat: '24'
    };

    const {
        orarioRequired,
        formatOrario,
        checkIntervalloFacoltativo,
        orarioLessThan,
        orarioMoreThan,
        onChangeOraInizio,
        onChangeOraFine,
        onChangeQuantita
    } = useRangeValidator<T>(nameInizio, nameFine, nameQuantita, optionalFields, onChangeRange, onNotANumber);

    // N.B. non posso usare una React.FC, vedi:
    // https://stackoverflow.com/questions/22573494/react-js-input-losing-focus-when-rerendering
    const fromHH = <InputCleave<T>
        options={options}
        rules={{
            validate: {
                required: v => checkIntervalloFacoltativo(v) || orarioRequired(v) || 'Campo obbligatorio',
                format: v => checkIntervalloFacoltativo(v) || formatOrario(v) || 'Utilizzare il formato hh:mm',
                lessThan: v => checkIntervalloFacoltativo(v) || orarioLessThan(v) || 'Orario maggiore uguale a ora fine'
            }
        }}
        label={showLabel ? 'Dalle ore' : undefined}
        labelClassName={labelsClassName}
        name={nameInizio}
        placeholder="hh:mm"
        disabled={disableFields}
        control={control}
        onChange={(v: string) => onChangeOraInizio(v)}
    />

    const toHH = <InputCleave<T>
        options={options}
        rules={{
            validate: {
                required: v => checkIntervalloFacoltativo(v) || orarioRequired(v) || 'Campo obbligatorio',
                format: v => checkIntervalloFacoltativo(v) || formatOrario(v) || 'Utilizzare il formato hh:mm',
                lessThan: v => checkIntervalloFacoltativo(v) || orarioMoreThan(v) || 'Orario minore uguale a ora inizio'
            }
        }}
        label={showLabel ? 'Alle ore' : undefined}
        labelClassName={labelsClassName}
        name={nameFine}
        placeholder="hh:mm"
        disabled={disableFields}
        control={control}
        onChange={(v: string) => onChangeOraFine(v)}
    />

    const quantitaOrariaInOreEMinuti = <InputCleave<T>
        options={options}
        rules={{
            validate: {
                format: v => checkIntervalloFacoltativo(v) || formatOrario(v) || 'Utilizzare il formato hh:mm',
                //@ts-ignore
                auxValidation: validate,
            }
        }}
        label={showLabel ? 'Quantità oraria' : undefined}
        labelClassName={labelsClassName}
        name={nameQuantita}
        placeholder="hh:mm"
        disabled={disableFields || !optionalFields}
        control={control}
        onChange={(v: string) => onChangeQuantita(v)}
        errorPortalId={errorPortalId}
    />

    const quantita = <InputCleave2<T>
        options={{
            allowNegative: false, allowedDecimalSeparators: [','], fixedDecimalScale: false, decimalScale: 2,
            isAllowed: ({floatValue}: NumberFormatValues) => {
                return floatValue ? floatValue <= 24 : true;
            }
        }}
        placeholder="hh.cc"
        valueAsNumber={true}
        rules={{
            //@ts-ignore
            validate // esempio: CausaleWFRecord.isRangeValid(causaleRichiesta!, v as number) || CausaleWFRecord.getRangeText2021(causaleRichiesta!)
        }}
        label={showLabel ? 'Quantità' : undefined}
        labelClassName={labelsClassName}
        name={nameQuantita}
        disabled={disableFields || !optionalFields}
        control={control}
        onChange={(v: string) => onChangeQuantita(v)}
        errorPortalId={errorPortalId}
    />

    return <>{children(fromHH, toHH, quantitaOrarieIn === 'MINUTI' ? quantitaOrariaInOreEMinuti : quantita)}</>
}

export default RangeHH;